// <copyright file="DataLakeExplorer.tsx">
// © 2024 Florida Department of Transportation
//
// The copyright to the computer program(s) and source code herein is
// the property of the Florida Department of Transportation.
// The program(s) and source code may be used and/or copied only with the
// written permission of the Florida Department of Transportation or
// in accordance with the terms and conditions stipulated in the
// agreement/contract under which the program(s) and source code have
// been supplied.
// </copyright>

import axios from "axios";
import { ProgressSpinner } from "primereact/progressspinner";
import { Toast } from "primereact/toast";
import React, { useState as _useState, useContext, useEffect } from "react";
import ComponentMenu from "src/components/ComponentMenu/ComponentMenu";
import styles from "src/components/DataLakeExplorer/DataLakeExplorer.module.css";
import DleGeneralInfo from "src/components/DataLakeExplorer/DleGeneralInfo";
import DleRecordType from "src/components/DataLakeExplorer/DleRecordType";
import ConfigContext from "src/context/ConfigContext";
import {
    LoadingStatus,
    displayLoadingError,
    getCookieJsonField,
} from "src/utils/SharedUtils";
import type { Menu } from "primereact/menu";
import type { MenuItem } from "primereact/menuitem";
import type { FC } from "react";
import type { IConfig } from "src/utils/ConfigLoader";

// Properties to initialize this component
interface DataLakeExplorerProps {
    useState?: typeof _useState; // optional hook override (for tests)
}

// API data model - MUST match API response for GET /dle-record-types
interface RecordTypesResponse {
    RecordTypes: RecordType[];
}
export interface RecordType {
    TableNamePrefix: string;
    CuratedSchema: string;
    TableDescription: string;
}

// Label for the default menu item to be displayed
const defaultMenuSelection = "General info";

// Info/error message popup
const toast = React.createRef<Toast>();

/**
 * Determines whether to display a progress spinner to indicate data loading, based on the
 * status of data loads and current user view selection
 * @param loadingStatus Status of data load
 * @returns Boolean indicator whether loading spinner should be shown
 */
function shouldDisplayLoadingSpinner(loadingStatus: LoadingStatus) {
    return loadingStatus === LoadingStatus.loading;
}

/**
 * Determines whether to display a data load error message
 * @param loadingStatus Status of data load
 * @returns Boolean indicator whether data load error message should be shown
 */
function shouldDisplayLoadingError(loadingStatus: LoadingStatus) {
    return loadingStatus === LoadingStatus.error;
}

/**
 * Retrieve DLE record types from db and update component menu items
 * @param configData
 * @param setMenuItems
 * @param setMenuSelected
 * @param setLoadingStatus
 */
async function fetchDleRecordTypes(
    configData: IConfig,
    setRecordTypes: React.Dispatch<React.SetStateAction<RecordType[]>>,
    setMenuItems: React.Dispatch<React.SetStateAction<MenuItem[]>>,
    setMenuSelected: React.Dispatch<React.SetStateAction<string | undefined>>,
    setLoadingStatus: React.Dispatch<React.SetStateAction<LoadingStatus>>
) {
    setLoadingStatus(LoadingStatus.loading);
    const rtResponse: RecordTypesResponse = {
        RecordTypes: [],
    };
    console.debug("Fetching DLE record types...");
    const headers = {
        headers: {
            Authorization: `Bearer ${getCookieJsonField(
                "DepSessionIdentity",
                "token"
            )}`,
        },
    };
    await axios
        .get<RecordTypesResponse>(
            `${configData.apiBaseUrl}/dle-record-types`,
            headers
        )
        .then((response) => {
            console.debug(`recordTypes: ${response.data}`);
            rtResponse.RecordTypes = response.data.RecordTypes;
        })
        .catch((error) => {
            const summary = "Error fetching DLE record types";
            const detail = error.response
                ? error.response.data.errorMessage
                : error.message;
            displayLoadingError(
                summary,
                detail,
                toast.current,
                setLoadingStatus
            );
        });
    // Set menu items from unique set of schemas, plus default (general info)
    const labels = [defaultMenuSelection].concat([
        ...new Set(rtResponse.RecordTypes.map((it) => it.CuratedSchema)),
    ]);
    const menuItems: MenuItem[] = [];
    labels.forEach((rt: string) => {
        menuItems.push({
            label: rt,
            command: () => {
                setMenuSelected(rt);
            },
        });
    });
    setRecordTypes(rtResponse.RecordTypes);
    setMenuItems(menuItems);
    setMenuSelected(defaultMenuSelection);
    setLoadingStatus(LoadingStatus.loaded);
}

/**
 * Determines whether to display a default menu item, e.g., general info
 * @param menuSelected User selected menu item or undefined
 * @returns True if default item should display, else false
 */
function shouldDisplayDefaultSelection(menuSelected: string | undefined) {
    return menuSelected && menuSelected === defaultMenuSelection;
}

/**
 * Determines whether to display the record type view
 * @param menuSelected User selected menu item or undefined
 * @returns True if record type should display, else false
 */
function shouldDisplayRecordType(menuSelected: string | undefined) {
    return menuSelected && menuSelected !== defaultMenuSelection;
}

/**
 * Focus on selected/default menu item, given the rendered menu container.
 * @param {HTMLElement} container The rendered menu element
 * @param {string|undefined} selectedItem The selected menu item label
 */
function focusMenuItem(
    container: HTMLElement,
    selectedItem: string | undefined
) {
    // Use CSS selector to get rendered menu items, then find the default
    const elem = Array.from(container.querySelectorAll("*[tabindex]")).find(
        (it) => it.textContent === (selectedItem ?? defaultMenuSelection)
    ) as HTMLElement | undefined;
    if (elem) {
        console.debug(`focus elem outer html: ${elem?.outerHTML || "null"}`);
        console.debug(`focus elem inner html: ${elem?.innerText || "null"}`);
        elem?.focus();
    }
}

/**
 * Data Lake Explorer (DLE) component
 * @param props Component props
 * @returns The component
 */
const DataLakeExplorer: FC<DataLakeExplorerProps> = (
    props: DataLakeExplorerProps
) => {
    // Initialize state & handlers
    const menuRef = React.useRef<Menu>(null);
    const configData = useContext(ConfigContext) as IConfig;
    const useState = props.useState ?? _useState;
    const [menuSelected, setMenuSelected] = useState<string | undefined>();
    const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
    const [recordTypes, setRecordTypes] = useState<RecordType[]>([]);
    const [loadingStatus, setLoadingStatus] = useState(LoadingStatus.preload);

    // Hook on component mount/unmount
    // useEffect should be used SPARINGLY due to CASCADE potential
    useEffect(() => {
        // CASCADES on setMenuSelected
        fetchDleRecordTypes(
            configData,
            setRecordTypes,
            setMenuItems,
            setMenuSelected,
            setLoadingStatus
        );
    }, []);

    // Hook on changes to selected record type menu item
    // useEffect should be used SPARINGLY due to CASCADE potential
    useEffect(() => {
        focusMenuItem(
            menuRef?.current?.getElement() as HTMLElement,
            menuSelected
        );
    }, [menuSelected]);

    // Breadcrumb items
    const breadcrumb = `Data Lake Explorer > ${menuSelected ?? "Loading..."}`;

    // Create components
    const className = `${styles.DataLakeExplorer} App-content`; // Must incldue App-content class
    return (
        <div className={className} data-testid="DataLakeExplorer">
            <Toast ref={toast} />
            <ComponentMenu
                focusRef={menuRef}
                title="Data Lake Explorer"
                menuItems={menuItems}
            />
            <div className="App-content-main">
                <div id="breadcrumb" className="flex flex-row gap-2 mb-2">
                    {breadcrumb}
                </div>
                {shouldDisplayLoadingSpinner(loadingStatus) && (
                    <ProgressSpinner
                        className="App-spinner"
                        aria-label="Loading"
                    />
                )}
                {shouldDisplayLoadingError(loadingStatus) && (
                    <div className="App-content-error" />
                )}
                {shouldDisplayDefaultSelection(menuSelected) && (
                    <DleGeneralInfo />
                )}
                {shouldDisplayRecordType(menuSelected) && (
                    <DleRecordType
                        recordType={menuSelected ?? "unknown"}
                        allRecordTypes={recordTypes}
                    />
                )}
            </div>
        </div>
    );
};

export default DataLakeExplorer;
